import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiStar } from 'react-icons/fi';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { LightboxImage } from '../../components/LightboxImage';
import './ApartmentReviews.css';

const REVIEWS_PER_PAGE = 5;

export function StarRow({ value, size = 16, interactive = false, onChange }) {
  return (
    <div className={`apt-rev-stars ${interactive ? 'apt-rev-stars--interactive' : ''}`} role={interactive ? 'group' : undefined} aria-label={interactive ? 'Rating' : undefined}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`apt-rev-star ${n <= value ? 'apt-rev-star--on' : ''}`}
          disabled={!interactive}
          onClick={() => interactive && onChange?.(n)}
          aria-pressed={interactive ? n <= value : undefined}
        >
          <FiStar size={size} aria-hidden />
        </button>
      ))}
    </div>
  );
}

export default function ApartmentReviews({ apartmentId, apartment, onRefreshApartment }) {
  const { user } = useAuth();
  const stats = apartment?.review_stats || {};
  const count = stats.count ?? 0;
  const avgRating = stats.avg_rating != null ? Number(stats.avg_rating) : null;
  const canSubmit = Boolean(apartment?.can_submit_review);
  const userHasReviewed = Boolean(apartment?.user_has_reviewed);

  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);

  const fetchPage = useCallback(
    async (page) => {
      setLoadError(null);
      setLoading(true);
      try {
        const res = await api.get(
          `/apartments/${apartmentId}/reviews?per_page=${REVIEWS_PER_PAGE}&page=${page}`,
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        const m = res.meta || null;
        setReviews(rows);
        setMeta(m);
      } catch (e) {
        setLoadError(e.message || 'Could not load reviews');
      } finally {
        setLoading(false);
      }
    },
    [apartmentId],
  );

  useEffect(() => {
    if (!apartmentId) return;
    setLoading(true);
    fetchPage(1);
  }, [apartmentId, fetchPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(false);
    if (!user) {
      setFormError('Please sign in to leave a review.');
      return;
    }
    if (comment.trim().length < 10) {
      setFormError('Please write at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(
        `/apartments/${apartmentId}/reviews`,
        { rating, comment: comment.trim() },
      );
      setComment('');
      setFormSuccess(true);
      await fetchPage(1);
      if (onRefreshApartment) await onRefreshApartment();
    } catch (err) {
      setFormError(err.message || 'Could not submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  const showForm = user && canSubmit && !userHasReviewed;
  const showEligibilityHint = user && !canSubmit && !userHasReviewed;
  const showLoginHint = !user && !userHasReviewed;

  const lastPage = meta?.last_page ?? 1;
  const currentPage = meta?.current_page ?? 1;

  return (
    <section className="apt-reviews" id="reviews" aria-labelledby="apt-reviews-heading">
      <div className="apt-reviews-head">
        <h2 id="apt-reviews-heading">Guest reviews</h2>
        {count > 0 && avgRating != null && (
          <div className="apt-reviews-summary" aria-label={`Average rating ${avgRating.toFixed(1)} out of 5, ${count} reviews`}>
            <span className="apt-reviews-avg">{avgRating.toFixed(1)}</span>
            <StarRow value={Math.round(avgRating)} />
            <span className="apt-reviews-count">{count} review{count !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {loading && reviews.length === 0 && (
        <p className="apt-reviews-muted">Loading reviews…</p>
      )}
      {loadError && <p className="apt-reviews-error">{loadError}</p>}

      {!loading && reviews.length === 0 && !loadError && (
        <p className="apt-reviews-muted">No reviews yet — be the first after your stay.</p>
      )}

      {reviews.length > 0 && (
        <ul className="apt-reviews-list">
          {reviews.map((r) => (
            <li key={r.id} className="apt-rev-card">
              <div className="apt-rev-card-inner">
                <div className="apt-rev-card-top">
                  <div className="apt-rev-author">
                    {r.author?.avatar_url ? (
                      <LightboxImage
                        src={r.author.avatar_url}
                        alt={r.author?.name ? `${r.author.name} profile photo` : 'Reviewer photo'}
                        className="apt-rev-avatar-lightbox"
                      >
                        <img src={r.author.avatar_url} alt="" className="apt-rev-avatar" />
                      </LightboxImage>
                    ) : (
                      <span className="apt-rev-avatar-fallback" aria-hidden>
                        {(r.author?.name || 'G').charAt(0)}
                      </span>
                    )}
                    <div className="apt-rev-author-text">
                      <p className="apt-rev-name">{r.author?.name || 'Guest'}</p>
                      <p className="apt-rev-date">{formatDate(r.created_at)}</p>
                    </div>
                  </div>
                  <StarRow value={r.rating} />
                </div>
                <p className="apt-rev-comment">{r.comment}</p>
                {r.admin_reply && (
                  <div className="apt-rev-reply">
                    <span className="apt-rev-reply-label">Luxistt</span>
                    <p>{r.admin_reply}</p>
                    {r.admin_replied_at && (
                      <span className="apt-rev-reply-meta">{formatDate(r.admin_replied_at)}</span>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {meta && lastPage > 1 && (
        <nav className="apt-reviews-pagination" aria-label="Review pages">
          <button
            type="button"
            className="apt-reviews-page-btn"
            disabled={loading || currentPage <= 1}
            onClick={() => fetchPage(currentPage - 1)}
          >
            Previous
          </button>
          <div className="apt-reviews-page-mid">
            <span className="apt-reviews-page-indicator">
              Page {currentPage} of {lastPage}
              {meta.total != null ? ` · ${meta.total} total` : ''}
            </span>
            {lastPage <= 9 && (
              <ol className="apt-reviews-page-dots">
                {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                  <li key={p}>
                    <button
                      type="button"
                      className={`apt-reviews-dot ${p === currentPage ? 'apt-reviews-dot--active' : ''}`}
                      disabled={loading || p === currentPage}
                      onClick={() => fetchPage(p)}
                      aria-label={`Go to page ${p}`}
                      aria-current={p === currentPage ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <button
            type="button"
            className="apt-reviews-page-btn"
            disabled={loading || currentPage >= lastPage}
            onClick={() => fetchPage(currentPage + 1)}
          >
            Next
          </button>
        </nav>
      )}

      <div className="apt-reviews-foot">
        {userHasReviewed && (
          <p className="apt-reviews-note apt-reviews-note--ok">You’ve already reviewed this apartment. Thank you.</p>
        )}

        {showEligibilityHint && (
          <p className="apt-reviews-note">
            Reviews unlock after you complete a stay here (not cancelled). You’ll see this form when you’re eligible.
          </p>
        )}

        {showLoginHint && (
          <p className="apt-reviews-note">
            <Link to="/login">Sign in</Link>
            {' '}
            to leave a review after a completed stay.
          </p>
        )}

        {showForm && (
          <form className="apt-rev-form" onSubmit={handleSubmit}>
            <h3 className="apt-rev-form-title">Write a review</h3>
            <p className="apt-rev-form-hint">Share an honest stay experience (min. 10 characters).</p>
            <label className="apt-rev-label">
              Rating
              <StarRow value={rating} interactive size={22} onChange={setRating} />
            </label>
            <label className="apt-rev-label">
              Your comment
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                minLength={10}
                maxLength={2000}
                placeholder="What stood out? Comfort, location, cleanliness…"
                required
              />
            </label>
            {formError && <p className="apt-reviews-error">{formError}</p>}
            {formSuccess && <p className="apt-reviews-success">Thanks — your review is live.</p>}
            <button type="submit" className="btn-primary apt-rev-submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit review'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
